import i18next from 'i18next';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { navigate } from '@dvag/dfs-orchestrator-client/messengers';
import { sharedAppRoutes } from '@dvag/dfs-constant-config/shared-app-names';
import { appNames } from '@dvag/dfs-constant-config/app-names';

import { buildPath } from 'service/buildPath';
import { advisoryDashboard } from 'config/asset/advisoryDashboard';
import { AdvisorySectionListData } from 'type/sectionList';
import {
  CATALOG_DASHBOARD_OPTION,
  CATALOG_SETTING_CODE,
  SimulationType,
} from 'config/asset/constant';
import { useGetSettingList } from 'hooks/useGetSettingList';

export interface UpperList {
  id: string;
  title: string;
  onClick: () => void;
  subtitle: string;
  img: string;
  show: boolean;
  coverPosition: string;
}

const navigateToMicroFrontend = (microAppId: string, path: string, nextUrl?: string) => {
  navigate(`/${microAppId}/${path}`, nextUrl);
};

const { beratungThumbnail, futureHHBThumbnail, recapThumbnail } = advisoryDashboard;

export const useScreenData = () => {
  const { householdId } = useParams();
  const { data: settingList } = useGetSettingList();

  const catalogUrl = useMemo(() => {
    const defaultView = settingList?.find(({ code }) => code === CATALOG_SETTING_CODE)
      ?.selectedOptions?.[0];

    const shouldShowDashboard = defaultView === CATALOG_DASHBOARD_OPTION;

    const path = shouldShowDashboard
      ? `${sharedAppRoutes.catalog.productCatalog}/catalog`
      : sharedAppRoutes.catalog.productCatalog;

    return buildPath(path, { householdId: String(householdId) });
  }, [settingList, householdId]);

  const navigateToExpensesSimulation = async () => {
    navigateToMicroFrontend(
      appNames.accounting,
      `simulation/${SimulationType.FUTURE_HOUSEHOLD_BUDGET}/haushalt/${householdId}/gateway`,
      `/${appNames.advisor}${buildPath('/haushalt/:householdId/beratung/', {
        householdId: householdId as string,
      })}`,
    );
  };

  const upperList: UpperList[] = [
    {
      id: 'Recap',
      title: i18next.t('advisoryDashboard.recap.title'),
      onClick: () =>
        navigateToMicroFrontend(
          appNames.sectionReport,
          `haushalt/${householdId}/analyse/gespraechszusammenfassung/recap`,
          `/${appNames.advisor}${buildPath('/haushalt/:householdId/beratung/', {
            householdId: householdId as string,
          })}`,
        ),
      subtitle: i18next.t('advisoryDashboard.recap.subtitle'),
      img: recapThumbnail,
      show: true,
      coverPosition: 'center 20%',
    },
    {
      id: 'Beratung',
      title: i18next.t('advisoryDashboard.beratung.title'),
      onClick: () => navigateToMicroFrontend(appNames.catalog, catalogUrl),
      subtitle: i18next.t('advisoryDashboard.beratung.subtitle'),
      img: beratungThumbnail,
      show: true,
      coverPosition: 'center 20%',
    },
    {
      id: 'FinancialSituation',
      title: i18next.t('advisoryDashboard.futureHHB.title'),
      onClick: navigateToExpensesSimulation,
      subtitle: i18next.t('advisoryDashboard.futureHHB.subtitle'),
      img: futureHHBThumbnail,
      show: true,
      coverPosition: 'center 35%',
    },
  ];
  const bottomList: AdvisorySectionListData[] = [
    {
      id: 'RentenInformation',
      title: i18next.t('advisoryDashboard.rentenInformation'),
      onClick: () => {
        navigateToMicroFrontend(
          appNames.accounting,
          `haushalt/${householdId}/analyse/basisdaten/staatliche-rente`,
          `/${appNames.advisor}${buildPath('/haushalt/:householdId/beratung/', {
            householdId: householdId as string,
          })}`,
        );
      },
      icon: 'sessel',
      show: true,
    },
    {
      id: 'FinancialBalanceSheet',
      title: i18next.t('advisoryDashboard.financialBalanceSheet'),
      onClick: () => {
        navigateToMicroFrontend(
          appNames.accounting,
          `haushalt/${householdId}/analyse/vermoegensbilanz`,
          `/${appNames.advisor}${buildPath('/haushalt/:householdId/beratung/', {
            householdId: householdId as string,
          })}`,
        );
      },
      icon: 'rechtsschutz-02',
      show: true,
    },
    {
      id: 'ContractOverview',
      title: i18next.t('advisoryDashboard.contractOverview.heading'),
      onClick: () => {
        navigateToMicroFrontend(
          appNames.accounting,
          `haushalt/${householdId}/analyse/haushaltsbudget/vertragsuebersicht`,
          `/${appNames.advisor}${buildPath('/haushalt/:householdId/beratung/', {
            householdId: householdId as string,
          })}`,
        );
      },
      icon: 'klemmbrett',
      show: true,
    },
  ];

  const buttonAppointment = {
    id: 'ButtonAppointment',
    label: i18next.t('advisoryDashboard.buttonAppointment'),
    onClick: () =>
      navigateToMicroFrontend(
        appNames.vpd,
        buildPath('haushalt/:householdId/analyse/terminabschluss/fuenf-schritte', {
          householdId: householdId as string,
        }),
      ),
  };
  return {
    upperList,
    bottomList,
    buttonAppointment,
  };
};
